"use client";

import { useEffect, useRef, useState } from "react";
import { onValue, ref } from "firebase/database";
import { db } from "@/config/firebase";
import { QUEUE_ROOT } from "@/config/queue";
import type { FilaResumo } from "@/config/types";
import { QueueListCard } from "@/components/queue/QueueListCard";
import { QueueListCardSkeleton } from "@/components/queue/QueueListCardSkeleton";
import { QueueVirtualHero } from "@/components/queue/QueueVirtualHero";
import {
  buildQueuesFromFirebase,
  getQueueStatus,
  getStoredMaxProgress,
  setStoredMaxProgress,
} from "./queueUtils";
import type { ExperienciaFila, QueuePersonEntry } from "./types";
import { useRouter } from "next/navigation";

export function FilasPageContent() {
  const [queues, setQueues] = useState<QueuePersonEntry[]>([]);
  const [experienciasById, setExperienciasById] = useState<
    Map<string, ExperienciaFila>
  >(new Map());
  const [loadingExperiencias, setLoadingExperiencias] = useState(true);
  const [loading, setLoading] = useState(true);
  const [cancellingKey, setCancellingKey] = useState<string | null>(null);
  const maxProgressRef = useRef<Record<string, number>>({});
  const router = useRouter();

  // Carrega experiências do backend.
  useEffect(() => {
    fetch("/api/experiencias")
      .then((response) => response.json())
      .then((data: ExperienciaFila[]) => {
        if (Array.isArray(data)) {
          setExperienciasById(new Map(data.map((exp) => [exp.id, exp])));
        }
      })
      .catch(() => setExperienciasById(new Map()))
      .finally(() => setLoadingExperiencias(false));
  }, []);

  // Listener em tempo real: filas do participante no Firebase.
  useEffect(() => {
    const participantEmail = localStorage.getItem("cazeemail") || "";
    if (!participantEmail) {
      router.push("/cadastro");
      return;
    }

    // progresso exibido nunca diminui, então guardamos o maior valor já atingido.
    maxProgressRef.current = getStoredMaxProgress();

    const unsubscribe = onValue(
      ref(db, QUEUE_ROOT),
      (snapshot) => {
        // se não existir fila, zera a lista e para de carregar.
        if (!snapshot.exists()) {
          setQueues([]);
          setLoading(false);
          return;
        }

        // Monta lista inicial de filas baseada no snapshot do Firebase.
        const rawQueues = buildQueuesFromFirebase(
          snapshot.val() as Record<string, FilaResumo>,
          participantEmail,
          experienciasById,
        );

        // progresso exibido nunca diminui, guarda o maior valor já atingido.
        const queuesWithLockedProgress = rawQueues.map((queue) => {
          const queueKey = `${queue.eventoId}-${queue.entryKey}`;
          const prevMax = maxProgressRef.current[queueKey] ?? 0;
          const displayedProgress = Math.max(prevMax, queue.progress);
          maxProgressRef.current[queueKey] = displayedProgress;
          return { ...queue, progress: displayedProgress };
        });

        // Atualiza o progresso máximo armazenado no localStorage.
        setStoredMaxProgress(maxProgressRef.current);

        // atualiza a lista de filas.
        setQueues(queuesWithLockedProgress);
        setLoading(false);
      },
      () => {
        // Em caso de erro no listener, limpa a lista e para de carregar.
        setQueues([]);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [experienciasById, router]);

  async function handleCancelQueue(queue: QueuePersonEntry) {
    // Identifica a fila que será cancelada.
    const queueKey = `${queue.eventoId}-${queue.entryKey}`;

    // Impede novo cancelamento enquanto a requisição está em andamento.
    setCancellingKey(queueKey);

    // Envia requisição para cancelar a fila
    try {
      const response = await fetch("/api/queue", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eventoId: queue.eventoId,
          entryKey: queue.entryKey,
          status: "canceled",
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error ?? "Erro ao cancelar fila");

      // Remove a fila da lista.
      setQueues((prev) =>
        prev.filter(
          (q) => q.eventoId !== queue.eventoId || q.entryKey !== queue.entryKey,
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setCancellingKey(null);
    }
  }

  return (
    <main className="min-h-[100dvh] bg-white text-black pt-5">
      <QueueVirtualHero />

      <div className="mx-auto w-full max-w-[380px] bg-white">
        <section className="px-5 pb-8 pt-[24px]">
          <h2 className="text-[22px] font-black uppercase leading-none text-[#2033ef]">
            Suas filas
          </h2>
          <p className="mt-[16px] text-[15px] leading-[1.2]">
            Acompanhe aqui as experiências em que você está na fila e veja
            quando chegar sua vez.
          </p>

          {/* loading */}
          {loading && (
            <div className="mt-[17px] flex flex-col gap-[20px]">
              <QueueListCardSkeleton />
              <QueueListCardSkeleton />
            </div>
          )}

          {/* Vazio */}
          {!loading && queues.length === 0 && (
            <p className="mt-[17px] rounded-[8px] bg-[#eeeeee] px-4 py-5 text-[13px] leading-[1.2]">
              Você não está em nenhuma fila.
            </p>
          )}

          {/* content */}
          {!loading && queues.length > 0 && (
            <div className="mt-[17px] flex flex-col gap-[20px]">
              {queues.map((queue) => {
                const experiencia = queue.experiencia;
                const queueStatus = getQueueStatus(queue);

                if (loadingExperiencias || !experiencia) {
                  return (
                    <QueueListCardSkeleton
                      key={`${queue.eventoId}-${queue.entryKey}`}
                    />
                  );
                }

                return (
                  <QueueListCard
                    key={`${queue.eventoId}-${queue.entryKey}`}
                    image={experiencia.image}
                    backgroundColor={experiencia.backgroundColor}
                    title={experiencia.title}
                    color={experiencia.colorName ?? "#2033ef"}
                    peopleAhead={queue.pessoasNaFrente}
                    progress={queue.progress}
                    status={queueStatus.status}
                    isCalled={queueStatus.isCalled}
                    isPaused={queue.filaStatus === "pausada"}
                    onCancel={() => handleCancelQueue(queue)}
                    cancelDisabled={
                      cancellingKey === `${queue.eventoId}-${queue.entryKey}`
                    }
                  />
                );
              })}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
